/**
 * ElementDetailPanel Component
 * Shows details of selected element: info, photos, anomalies
 */

import { MapPin, Camera, AlertTriangle, X, ExternalLink } from 'lucide-react';
import { usePhotoStore } from '../../store/photoStore';
import { useAnomalyStore } from '../../store/anomalyStore';

interface ElementDetailPanelProps {
  elementId: string; // UUID
  elementType: 'SEGMENT' | 'SUPPORT' | 'STATION' | 'BAY';
  elementCode: string;
  coordinates?: { lat: number; lng: number } | { lat: number; lng: number }[];
  photos?: any[]; // Optional: pass photos from parent, otherwise use store
  onClose: () => void;
  onPhotoClick: (photoId: string) => void;
}

export function ElementDetailPanel({
  elementId,
  elementType,
  elementCode,
  coordinates,
  photos: propsPhotos,
  onClose,
  onPhotoClick,
}: ElementDetailPanelProps) {
  const { photos: storePhotos } = usePhotoStore();
  const { getAnomaliesByElement } = useAnomalyStore();

  // Use photos from props if provided, otherwise from store
  const allPhotos = propsPhotos || storePhotos;
  
  // Filter photos by element (elementId is already a UUID string)
  const elementPhotos = allPhotos.filter(p => p.elementId === elementId);
  // TODO: Update getAnomaliesByElement to accept string UUID
  const elementAnomalies = getAnomaliesByElement(elementId as any);

  // Get coordinate display
  const coordDisplay = coordinates
    ? Array.isArray(coordinates)
      ? `${coordinates.length} points`
      : `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`
    : 'N/A';

  return (
    <div className="absolute right-4 top-4 w-96 bg-white dark:bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-xl border border-slate-200 dark:border-slate-700/50 z-[1000] max-h-[calc(100vh-2rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800 rounded-t-lg">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{elementType}</span>
          </div>
          <h3 className="text-lg font-mono font-semibold text-slate-800 dark:text-slate-100">{elementCode}</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
          title="Close panel"
        >
          <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto dark-scrollbar">
        {/* Info Section */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700/50">
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="w-4 h-4 text-slate-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-mono font-medium text-slate-600 dark:text-slate-300">Coordinates</div>
              <div className="font-mono text-slate-500 dark:text-slate-400 text-xs">{coordDisplay}</div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 p-4 border-b border-slate-200 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/50">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-blue-500 dark:text-blue-400" />
            <div>
              <div className="text-2xl font-mono font-bold text-slate-800 dark:text-slate-100">{elementPhotos.length}</div>
              <div className="text-xs font-mono text-slate-500">Photos</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-amber-400" />
            <div>
              <div className="text-2xl font-mono font-bold text-slate-800 dark:text-slate-100">{elementAnomalies.length}</div>
              <div className="text-xs font-mono text-slate-500">Anomalies</div>
            </div>
          </div>
        </div>

        {/* Photos Section */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700/50">
          <h4 className="text-xs font-mono font-semibold text-slate-600 dark:text-slate-300 mb-3 flex items-center gap-2 uppercase tracking-wider">
            <Camera className="w-4 h-4" />
            Photos ({elementPhotos.length})
          </h4>
          
          {elementPhotos.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm font-mono">
              No photos attached to this element
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {elementPhotos.map((photo) => (
                <button
                  key={photo.id}
                  onClick={() => onPhotoClick(photo.id)}
                  className="relative aspect-square rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-700 hover:ring-2 hover:ring-blue-400 transition-all group"
                >
                  {(photo.thumbnailUrl || photo.url) ? (
                    <img
                      src={photo.thumbnailUrl || photo.url}
                      alt={photo.filename}
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement?.querySelector('.camera-fallback')?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`camera-fallback w-full h-full flex items-center justify-center ${(photo.thumbnailUrl || photo.url) ? 'hidden' : ''}`}>
                    <Camera className="w-6 h-6 text-slate-400 dark:text-slate-500" />
                  </div>
                  
                  {/* Camera badge */}
                  <span className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-medium ${
                    photo.camera === 'FRONTE' 
                      ? 'bg-blue-500/80 text-white' 
                      : 'bg-amber-500/80 text-white'
                  }`}>
                    {photo.camera}
                  </span>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                    <ExternalLink className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Anomalies Section */}
        <div className="p-4">
          <h4 className="text-xs font-mono font-semibold text-slate-600 dark:text-slate-300 mb-3 flex items-center gap-2 uppercase tracking-wider">
            <AlertTriangle className="w-4 h-4" />
            Anomalies ({elementAnomalies.length})
          </h4>
          
          {elementAnomalies.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm font-mono">
              No anomalies marked yet
            </div>
          ) : (
            <div className="space-y-2">
              {elementAnomalies.map((anomaly) => (
                <div
                  key={anomaly.id}
                  className="p-3 bg-slate-100 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600/50 hover:border-slate-300 dark:hover:border-slate-500/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-1">
                    <span className="font-mono text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {anomaly.issueCode}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-mono font-medium ${
                      anomaly.evaluationLevel === 3
                        ? 'bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/30'
                        : anomaly.evaluationLevel === 2
                        ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30'
                        : 'bg-green-500/20 text-green-600 dark:text-green-400 border border-green-500/30'
                    }`}>
                      LVL {anomaly.evaluationLevel}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">{anomaly.issueDescription}</p>
                  {anomaly.note && (
                    <p className="text-xs text-slate-400 dark:text-slate-500 italic">"{anomaly.note}"</p>
                  )}
                  <div className="text-xs font-mono text-slate-400 dark:text-slate-600 mt-2">
                    Photo: {anomaly.photoFileName}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer actions */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800 rounded-b-lg">
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg font-mono font-medium transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}
